import _uniqBy from 'lodash/uniqBy'
import _get from 'lodash/get'
import _uniq from 'lodash/uniq'
import PAID_LEAVE from '../../constants'

export const usableTopicFilters = (focus) => ['product', 'jurisdiction']
  .some((property) => _get(focus, `${property}.id`, false))

const topicSort = ({ sortOrder: first }, { sortOrder: second }) => {
  if (first > second) return 1
  if (first < second) return -1
  return 0
}

export const getTopicsByFilters = ({ topics, topicFilters }) => {
  const { jurisdiction, product } = topicFilters
  return (!usableTopicFilters(topicFilters) || !topics)
    ? []
    : topics
      .filter((topic) => (
        topic.jurisdiction.id === jurisdiction.id)
      && topic.product.id === product.id
      && topic.context)
      .sort(topicSort)
}

export const getLocationFilteredTopics = ({ location: { topics }, topicFilters }) => {
  let filteredTopics
  try {
    filteredTopics = (!usableTopicFilters(topicFilters) || !topics)
      ? []
      : getTopicsByFilters({ topics, topicFilters })
        .sort(topicSort)
  } catch (error) {
    console.error(error)
  }

  return filteredTopics
}

export const getFilteredSummaries = ({ topics, topicFilters }) => {
  if (!usableTopicFilters(topicFilters) || !topics) return []
  return _uniqBy(
    getTopicsByFilters({ topics, topicFilters })
      .filter((topic) => topic.summary.template)
      .map((topic) => ({
        id: topic.id,
        title: topic.topic,
        summary: topic.summary.template
      })), 'id'
  )
}

export const topicsByJurisdiction = (topics, location) => {
  return topics.reduce((accumulator, topic) => {
    const jurisdiction = accumulator.find(({ id }) => id === topic.jurisdiction)
    if (jurisdiction) {
      jurisdiction.topics.push(topic)
    } else {
      const newJurisdiction = location.locales.find(({ id }) => id === topic.jurisdiction)
      accumulator.push({
        id: newJurisdiction.id,
        name: newJurisdiction.name,
        state: newJurisdiction.state,
        country: newJurisdiction.country,
        jurisdiction: newJurisdiction.jurisdiction,
        topics: [topic]
      })
    }
    return accumulator
  }, [])
}

export const groupTopicsByJurisdiction = (locationData) => {
  const jurisdictionMap = _uniqBy(locationData
    .locales
    .flatMap((locale) => locale.topics)
    .map((locale) => ({
      topicId: locale.id,
      jurisdictionId: locale.jurisdiction
    })), 'topicId')

  const mappedTopics = locationData
    .topics
    .filter((topic) => !topic.jurisdiction)
    .map((topic) => (
      {
        ...topic,
        jurisdiction:
        jurisdictionMap
          .find(({ topicId }) => topic.id === topicId)
          .jurisdictionId
      }))

  return {
    ...locationData,
    topics: mappedTopics,
    topicsByJurisdiction: topicsByJurisdiction(mappedTopics)
  }
}

const JurisdictionHierarchy = [
  'city',
  'county',
  'state',
  'federal'
]

export const getMostGranularJurisdiction = (topics) => topics
  .map((topic) => topic.jurisdiction)
  .reduce((currentMostGranularJurisdiction, nextJurisdiction) => {
    const nextJurisdictionHierarchyIndex = JurisdictionHierarchy.indexOf(nextJurisdiction.jurisdiction.toLowerCase())
    const currentHierarchyIndex = currentMostGranularJurisdiction
      ? JurisdictionHierarchy.indexOf(currentMostGranularJurisdiction.jurisdiction.toLowerCase())
      : JurisdictionHierarchy.length + 1

    return nextJurisdictionHierarchyIndex < currentHierarchyIndex
      ? nextJurisdiction
      : currentMostGranularJurisdiction
  }, null)

export const addFederalIfApplicable = (applicableJurisdictions, topics) => {
  const mostGranularJurisdiction = getMostGranularJurisdiction(topics)

  const foundFederal = applicableJurisdictions.map((jurisdiction) => jurisdiction.name.toLowerCase()).includes('federal')

  return foundFederal
    ? applicableJurisdictions
    : [mostGranularJurisdiction].concat(getFederalJurisdiction(topics))
}

export const getFederalJurisdiction = (topics) => {
  const federal = topics.find(({ jurisdiction }) => jurisdiction.name.toLowerCase() === 'federal')
  return federal ? federal.jurisdiction : []
}

export const getFilteredJurisdictionsByProduct = ({ location, productId }) => {
  return (!location.topics || !productId)
    ? []
    : [getMostGranularJurisdiction(location.topics
      .filter(({ product }) => product.id === productId))]
}

export const getFilteredJurisdictions = ({ location, topicFilters }) => {
  return (!usableTopicFilters(topicFilters) || !location.topics)
    ? []
    : addFederalIfApplicable(
      getFilteredJurisdictionsByProduct({ location, productId: topicFilters.product.id }),
      location.topics.filter(({ product }) => product.id === topicFilters.product.id)
    )
}

export const getProductIndex = (p) => PAID_LEAVE.PRODUCTS.findIndex(({ id }) => id === p) + 1

export const getAllProducts = (topics) => {
  return _uniqBy(topics, 'product')
    .map(({ product: { id: product } }) => PAID_LEAVE.PRODUCTS.find(({ id: productId }) => product === productId))
    .sort((productA, productB) => {
      const productAIndex = getProductIndex(productA)
      const productBIndex = getProductIndex(productB)
      if (productAIndex > productBIndex) return 1
      if (productAIndex === productBIndex) return 0
      if (productAIndex < productBIndex) return -1
      return 0
    })
}

export const getFilteredProducts = ({ location, topicFilters }) => {
  return (!usableTopicFilters(topicFilters) || !_get(location, 'topics', false))
    ? getAllProducts(location.topics)
    : location.topics
      .reduce((filteredProductList, topic) => {
        const productInResult = filteredProductList.find(({ id }) => id === topic.product.id)
        if (!productInResult) {
          filteredProductList.push(topic.product)
        }
        return filteredProductList
      }, [])
}

export const getFilteredNavItems = ({ location, topicFilters }) => {
  return (!usableTopicFilters(topicFilters) || !_get(location, 'topics', false))
    ? []
    : _uniq(
      getLocationFilteredTopics({ location, topicFilters })
        .map(({ topic }) => topic)
    )
}
